import { useMemo, useState } from 'react';
import { Link } from '@tanstack/react-router';
import type { MeetingResponse } from '@orion/contracts';
import { Button, Checkbox, Dialog } from '@orion/ui';
import { PageHeader } from '../../app/shell.js';
import { ApiError } from '../../lib/api-client.js';
import { useAuth } from '../../lib/auth.js';
import { disambiguateNames } from '../../lib/disambiguate-names.js';
import { useToast } from '../../lib/toast.js';
import { useAssignableMembersQuery } from '../me/api.js';
import { useCreateLeadFromMeeting, useMeetingsQuery } from './api.js';

type ViewMode = 'month' | 'week';

function startOfWeek(date: Date): Date {
  const d = new Date(date);
  const day = d.getDay();
  const mondayDiff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + mondayDiff);
  d.setHours(0, 0, 0, 0);
  return d;
}

function addDays(date: Date, n: number): Date {
  const d = new Date(date);
  d.setDate(d.getDate() + n);
  return d;
}

function dateKey(date: Date): string {
  return date.toISOString().slice(0, 10);
}

function monthGridDays(anchor: Date): Date[] {
  const firstOfMonth = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
  const start = startOfWeek(firstOfMonth);
  const lastOfMonth = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0);
  const end = startOfWeek(lastOfMonth);
  const weeks = Math.round((end.getTime() - start.getTime()) / (7 * 86_400_000)) + 1;
  return Array.from({ length: weeks * 7 }, (_, i) => addDays(start, i));
}

const WEEKDAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function MeetingPill({ meeting, onClick }: { meeting: MeetingResponse; onClick: () => void }) {
  const canceled = meeting.status === 'canceled';
  const unmatched = !meeting.leadId && !meeting.contactId;
  return (
    <button
      type="button"
      onClick={onClick}
      title={unmatched ? "Not linked to a lead or contact yet — open to create one" : undefined}
      className={
        'w-full truncate rounded-xs px-1.5 py-0.5 text-left text-[11px] font-medium ' +
        (canceled ? 'bg-surface-subtle text-text-muted line-through' : unmatched ? 'border border-dashed border-border-strong bg-surface text-text-secondary' : 'bg-amber-soft text-amber-text')
      }
    >
      {new Date(meeting.startTime).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })} {meeting.title}
    </button>
  );
}

/** "Manage view" — which team members' and which connected calendars'
 * meetings show on the grid. A plain checkbox list, not `Menu`: picking one
 * option must not close the panel the way a single-select menu item does.
 * Calendly is the only connected-calendar source today, so that section is
 * one checkbox — still its own group so a second provider (Google/Outlook)
 * slots in as another row later. */
function ManageViewPanel({
  members,
  hiddenOwnerIds,
  onToggleOwner,
  calendlyVisible,
  onToggleCalendly,
}: {
  members: { id: string; label: string }[];
  hiddenOwnerIds: Set<string>;
  onToggleOwner: (id: string) => void;
  calendlyVisible: boolean;
  onToggleCalendly: () => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative inline-block">
      <Button variant="secondary" size="sm" onClick={() => setOpen((o) => !o)}>
        Manage view
      </Button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full z-50 mt-1 w-64 overflow-hidden rounded-dialog border border-border bg-surface p-3 shadow-dialog">
            <div className="flex flex-col gap-2">
              <span className="text-[11px] font-semibold uppercase tracking-[0.06em] text-text-muted">Calendars</span>
              <Checkbox label="Calendly" checked={calendlyVisible} onChange={onToggleCalendly} />
            </div>
            <div className="mt-3 flex flex-col gap-2 border-t border-border pt-3">
              <span className="text-[11px] font-semibold uppercase tracking-[0.06em] text-text-muted">Team</span>
              {members.length === 0 ? (
                <span className="text-xs text-text-muted">No teammates to show.</span>
              ) : (
                <div className="flex max-h-56 flex-col gap-1.5 overflow-y-auto">
                  {members.map((m) => (
                    <Checkbox key={m.id} label={m.label} checked={!hiddenOwnerIds.has(m.id)} onChange={() => onToggleOwner(m.id)} />
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export function CalendarPage() {
  const [view, setView] = useState<ViewMode>('month');
  const [anchor, setAnchor] = useState(() => new Date());
  const [selected, setSelected] = useState<MeetingResponse | null>(null);
  const [hiddenOwnerIds, setHiddenOwnerIds] = useState<Set<string>>(new Set());
  const [calendlyVisible, setCalendlyVisible] = useState(true);
  const createLeadMutation = useCreateLeadFromMeeting();
  const toast = useToast();
  const { hasPermission } = useAuth();

  const { data: members } = useAssignableMembersQuery();
  const memberLabels = useMemo(() => disambiguateNames(members ?? []), [members]);
  const memberOptions = useMemo(
    () => (members ?? []).map((m) => ({ id: m.id, label: memberLabels.get(m.id) ?? m.displayName })),
    [members, memberLabels],
  );

  const toggleOwner = (id: string) =>
    setHiddenOwnerIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const days = useMemo(() => (view === 'month' ? monthGridDays(anchor) : Array.from({ length: 7 }, (_, i) => addDays(startOfWeek(anchor), i))), [view, anchor]);
  const from = days[0]!;
  const to = addDays(days[days.length - 1]!, 1);

  const { data: meetings, isLoading } = useMeetingsQuery({ from: from.toISOString(), to: to.toISOString() });

  const visibleMeetings = useMemo(
    () => (calendlyVisible ? (meetings ?? []).filter((m) => !m.owner || !hiddenOwnerIds.has(m.owner.id)) : []),
    [meetings, hiddenOwnerIds, calendlyVisible],
  );

  const byDay = useMemo(() => {
    const map = new Map<string, MeetingResponse[]>();
    for (const m of visibleMeetings) {
      const key = dateKey(new Date(m.startTime));
      map.set(key, [...(map.get(key) ?? []), m]);
    }
    return map;
  }, [visibleMeetings]);

  const title = view === 'month'
    ? anchor.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
    : `${from.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${addDays(to, -1).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;

  const step = (n: number) => setAnchor((prev) => (view === 'month' ? new Date(prev.getFullYear(), prev.getMonth() + n, 1) : addDays(prev, n * 7)));

  const today = dateKey(new Date());

  const createLead = async () => {
    if (!selected) return;
    try {
      const updated = await createLeadMutation.mutateAsync(selected.id);
      setSelected(updated);
      toast.success('Lead created from this meeting.');
    } catch (err) {
      const detail = err instanceof ApiError ? (err.problem.detail ?? err.problem.title) : 'Failed to create lead.';
      toast.error(detail);
    }
  };

  return (
    <>
      <PageHeader
        breadcrumb={[{ label: 'Calendar' }]}
        actions={
          <div className="flex items-center gap-2">
            <Button variant="secondary" size="sm" onClick={() => setAnchor(new Date())}>
              Today
            </Button>
            <Button variant="secondary" size="sm" onClick={() => setView(view === 'month' ? 'week' : 'month')}>
              {view === 'month' ? 'Week view' : 'Month view'}
            </Button>
            <ManageViewPanel
              members={memberOptions}
              hiddenOwnerIds={hiddenOwnerIds}
              onToggleOwner={toggleOwner}
              calendlyVisible={calendlyVisible}
              onToggleCalendly={() => setCalendlyVisible((v) => !v)}
            />
          </div>
        }
      />
      <main className="flex flex-1 flex-col gap-3 overflow-auto px-7 pb-6 pt-5">
        <div className="flex items-center gap-3">
          <Button variant="secondary" size="sm" aria-label="Previous" onClick={() => step(-1)}>
            ←
          </Button>
          <Button variant="secondary" size="sm" aria-label="Next" onClick={() => step(1)}>
            →
          </Button>
          <h1 className="text-lg font-semibold text-ink">{title}</h1>
          {isLoading && <span className="text-xs text-text-muted">Loading…</span>}
        </div>

        <div className="grid grid-cols-7 overflow-hidden rounded-control border border-border">
          {WEEKDAY_LABELS.map((label) => (
            <div key={label} className="border-b border-border bg-surface-subtle px-2 py-1.5 text-center text-[11px] font-semibold uppercase tracking-[0.06em] text-text-muted">
              {label}
            </div>
          ))}
          {days.map((day) => {
            const key = dateKey(day);
            const dayMeetings = byDay.get(key) ?? [];
            const inMonth = view === 'week' || day.getMonth() === anchor.getMonth();
            const isToday = key === today;
            return (
              <div
                key={key}
                className={
                  'flex min-h-28 flex-col gap-1 border-b border-r border-border p-1.5 last:border-r-0 ' +
                  (inMonth ? 'bg-surface' : 'bg-surface-subtle')
                }
              >
                <span className={'text-xs font-medium ' + (isToday ? 'flex size-5 items-center justify-center rounded-full bg-primary text-white' : inMonth ? 'text-text-secondary' : 'text-text-muted')}>
                  {day.getDate()}
                </span>
                {dayMeetings.slice(0, 3).map((m) => (
                  <MeetingPill key={m.id} meeting={m} onClick={() => setSelected(m)} />
                ))}
                {dayMeetings.length > 3 && <span className="text-[11px] text-text-muted">+{dayMeetings.length - 3} more</span>}
              </div>
            );
          })}
        </div>
      </main>

      <Dialog
        open={Boolean(selected)}
        title={selected?.title ?? ''}
        onClose={() => setSelected(null)}
        actions={
          selected?.leadId ? (
            <Link to="/leads/$leadId" params={{ leadId: selected.leadId }}>
              <Button size="sm" onClick={() => setSelected(null)}>
                View lead
              </Button>
            </Link>
          ) : selected && !selected.contactId && hasPermission('crm.leads.create') ? (
            <Button size="sm" loading={createLeadMutation.isPending} onClick={createLead}>
              Create lead
            </Button>
          ) : undefined
        }
      >
        {selected && (
          <div className="flex flex-col gap-2 text-sm text-text-secondary">
            <div>
              <span className="font-medium text-ink">When: </span>
              {new Date(selected.startTime).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' })}
              {selected.endTime && ` – ${new Date(selected.endTime).toLocaleTimeString('en-US', { timeStyle: 'short' })}`}
            </div>
            {selected.location && (
              <div>
                <span className="font-medium text-ink">Where: </span>
                {selected.location}
              </div>
            )}
            <div>
              <span className="font-medium text-ink">Booked by: </span>
              {selected.inviteeName ?? selected.inviteeEmail}
            </div>
            {selected.owner && (
              <div>
                <span className="font-medium text-ink">Owner: </span>
                {selected.owner.displayName}
              </div>
            )}
            {selected.status === 'canceled' && <div className="font-medium text-danger">Canceled</div>}
            {!selected.leadId && !selected.contactId && (
              <div className="text-text-muted">Not linked to a lead or contact yet.</div>
            )}
          </div>
        )}
      </Dialog>
    </>
  );
}
